import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, Download, FileSpreadsheet, Loader2, Package, Upload } from 'lucide-react';
import { useView } from '../contexts/ViewContext';
import { useFormatters } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { partsAPI } from '../lib/api/parts';
import type { Part, PartUploadResponse, PartStats } from '../lib/api/types/parts';
import {
  shouldShowExtraColumns,
  shouldShowScheduleColumns,
} from '../config/parts-master-columns';
import PartsMasterPagination from '../components/parts-master/PartsMasterPagination';
import PartsMasterFilters from '../components/parts-master/PartsMasterFilters';
import PartsMasterColumnsButton from '../components/parts-master/PartsMasterColumnsButton';
import PartsMasterUploadDialog from '../components/parts-master/PartsMasterUploadDialog';
import PartsMasterTable, { type SortDir } from '../components/parts-master/PartsMasterTable';
import {
  buildAllColumnDescriptors,
  usePartsMasterVisibleColumns,
} from '../components/parts-master/usePartsMasterVisibleColumns';
import { usePartsExport } from '../components/parts-master/usePartsExport';

const ALL_VALUE = 'all';

export default function PartsMaster() {
  const { getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const { formatDate } = useFormatters();
  const { user } = useAuth();
  const importHandler = user?.import_handler ?? null;

  const [parts, setParts] = useState<Part[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const [stats, setStats] = useState<PartStats | null>(null);

  // Filters
  // Default to active-only: the parts feed marks parts inactive when they go
  // inactive in the ERP, and the Parts Master should show only active parts by
  // default. Users can still pick "All" or "Inactive" in the status filter.
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [commodityGroupFilter, setCommodityGroupFilter] = useState<string>(ALL_VALUE);
  const [warehouseFilter, setWarehouseFilter] = useState<string>(ALL_VALUE);

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<PartUploadResponse | null>(null);

  const toggleSort = useCallback((key: string) => {
    setPage(1);
    if (sortField !== key) {
      setSortField(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortField(null);
      setSortDir(null);
    }
  }, [sortField, sortDir]);

  // Whether the schedule + extras blocks should render at all
  const showExtras = shouldShowExtraColumns(parts, importHandler);
  const showSchedule = shouldShowScheduleColumns(parts, importHandler);

  // Distinct warehouses come from the /stats/ endpoint so the dropdown lists
  // every warehouse across the full filtered set (not just the visible page).
  // PNA tenants typically have 5+ warehouses; deriving from the page's parts
  // missed any warehouse that didn't appear on page 1.
  const warehouseOptions = useMemo(
    () => stats?.warehouses ?? [],
    [stats?.warehouses],
  );

  const showWarehouseFilter = importHandler === 'pna' && warehouseOptions.length > 0;

  // Column visibility — pass `parts` so the extras list is trimmed to
  // columns that actually have data on the current page (handler must
  // already gate `showExtras`).
  const allColumnDescriptors = useMemo(
    () => buildAllColumnDescriptors(showSchedule, showExtras, parts),
    [showSchedule, showExtras, parts],
  );
  const allColumnIds = useMemo(
    () => allColumnDescriptors.map(c => c.id),
    [allColumnDescriptors],
  );
  const { visible, order, toggle, showAll, reset, reorder } = usePartsMasterVisibleColumns(allColumnIds);

  // Build the params used by both list fetch and export, so they share a single
  // source of truth. `page` / `page_size` are added by callers — see fetchParts
  // and the export hook.
  const buildBaseParams = useCallback((): Record<string, string | number | boolean | undefined> => {
    const viewParams = getViewQueryParams();
    const orderingParam = sortField && sortDir
      ? (sortDir === 'desc' ? `-${sortField}` : sortField)
      : 'part_number';
    const params: Record<string, string | number | boolean | undefined> = {
      ordering: orderingParam,
      ...viewParams,
    };
    if (debouncedSearch) params['search'] = debouncedSearch;
    if (statusFilter === 'active') params['is_active'] = 'true';
    else if (statusFilter === 'inactive') params['is_active'] = 'false';
    if (commodityGroupFilter && commodityGroupFilter !== ALL_VALUE) {
      params['commodity_type'] = commodityGroupFilter;
    }
    if (warehouseFilter && warehouseFilter !== ALL_VALUE) {
      params['default_warehouse'] = warehouseFilter;
    }
    return params;
  }, [getViewQueryParams, sortField, sortDir, debouncedSearch, statusFilter, commodityGroupFilter, warehouseFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const viewParams = getViewQueryParams();
      const response = await partsAPI.stats(viewParams);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [getViewQueryParams]);

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...buildBaseParams(), page, page_size: pageSize };
      const response = await partsAPI.list(params);
      setParts(response.data.results);
      setTotalCount(response.data.count);
    } catch (err) {
      console.error('Failed to fetch parts:', err);
    } finally {
      setLoading(false);
    }
  }, [buildBaseParams, page, pageSize]);

  useEffect(() => {
    if (viewContextLoading) return;
    fetchStats();
  }, [fetchStats, viewContextLoading, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  useEffect(() => {
    if (viewContextLoading) return;
    fetchParts();
  }, [fetchParts, viewContextLoading, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, commodityGroupFilter, warehouseFilter]);

  // Export hook — pulls the full filtered set and writes .xlsx
  const { exporting, exportToXlsx } = usePartsExport({
    buildBaseParams,
    visibleIds: visible,
    showSchedule,
    showExtras,
  });

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const response = await partsAPI.upload(uploadFile);
      setUploadResult(response.data);
      fetchParts();
      fetchStats();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      const axiosErr = err as Record<string, Record<string, Record<string, string>>>;
      setUploadResult({
        total_rows: 0,
        created: 0,
        updated: 0,
        errors: [{ row: 0, error: axiosErr?.['response']?.['data']?.['error'] || message }],
        error_count: 1,
      });
    } finally {
      setUploading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startRow = totalCount === 0 ? 0 : ((page - 1) * pageSize) + 1;
  const endRow = Math.min(page * pageSize, totalCount);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Parts Master</h1>
          <p className="text-muted-foreground">
            Manage your parts catalogue and material master data
          </p>
        </div>
        <Button onClick={() => { setUploadOpen(true); setUploadFile(null); setUploadResult(null); }}>
          <Upload className="mr-2 h-4 w-4" />
          Upload Parts
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Parts</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats?.total ?? 0).toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Parts</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats?.active ?? 0).toLocaleString()}</div>
            {stats && stats.inactive > 0 && (
              <p className="text-xs text-muted-foreground mt-1">{stats.inactive.toLocaleString()} inactive</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commodity Types</CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats?.commodity_groups_count ?? 0).toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <PartsMasterFilters
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        commodityGroupFilter={commodityGroupFilter}
        onCommodityChange={setCommodityGroupFilter}
        commodityOptions={stats?.commodity_groups ?? []}
        pageSize={pageSize}
        onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
        showWarehouseFilter={showWarehouseFilter}
        warehouseFilter={warehouseFilter}
        onWarehouseChange={setWarehouseFilter}
        warehouseOptions={warehouseOptions}
        toolbarSlot={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToXlsx}
              disabled={exporting || totalCount === 0}
              title="Export the full filtered set to Excel"
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
            <PartsMasterColumnsButton
              columns={allColumnDescriptors}
              visible={visible}
              order={order}
              onToggle={toggle}
              onSelectAll={showAll}
              onReset={reset}
              onReorder={reorder}
            />
          </>
        }
      />

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading parts...</span>
            </div>
          ) : parts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No parts found</h3>
              <p className="text-muted-foreground mt-1">
                {search || statusFilter !== ALL_VALUE || commodityGroupFilter !== ALL_VALUE || warehouseFilter !== ALL_VALUE
                  ? 'Try adjusting your search or filters'
                  : 'Upload an Excel file to get started'}
              </p>
              {!search && statusFilter === ALL_VALUE && commodityGroupFilter === ALL_VALUE && warehouseFilter === ALL_VALUE && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => { setUploadOpen(true); setUploadFile(null); setUploadResult(null); }}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Parts
                </Button>
              )}
            </div>
          ) : (
            <>
              <PartsMasterPagination
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                startRow={startRow}
                endRow={endRow}
                onPageChange={setPage}
              />
              <PartsMasterTable
                parts={parts}
                sortField={sortField}
                sortDir={sortDir}
                onToggleSort={toggleSort}
                showExtras={showExtras}
                showSchedule={showSchedule}
                visible={visible}
                order={order}
                formatDate={formatDate}
              />
              <PartsMasterPagination
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                startRow={startRow}
                endRow={endRow}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <PartsMasterUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        uploadFile={uploadFile}
        onFileSelected={(f) => { setUploadFile(f); setUploadResult(null); }}
        uploading={uploading}
        uploadResult={uploadResult}
        onUpload={handleUpload}
        onDone={() => { setUploadOpen(false); setUploadResult(null); setUploadFile(null); }}
      />
    </div>
  );
}
